/**
 * Live company-base gravity for Inbox mint.
 *
 * Origins and destination mix follow the desk snapshot (primary + extras),
 * not the old Celtic / Port Hawkesbury (CYPD) fiction pad.
 * Canada stays legal. US-only is not forced. A former home is not a familiar dest.
 */
import { normalizeIcao } from "./airportDoctrine";
import { resolveCoords } from "./airports";
import { haversineNm } from "./geo";
import { destRegionOf, type DestRegion } from "./inboxMintMemory";
import type { SceneryAirport } from "./types";

/** Parked tail counts as an RFQ origin when it sits on or very near a current base. */
export const NEAR_BASE_ORIGIN_NM = 80;

const HOME_REGION_NM = 550;
const MEDIUM_NM = 1000;
const SHORT_STAGE_NM = 120;

const CANADA_REGIONS = new Set<DestRegion>(["maritimes", "quebec", "ontario", "prairies", "west"]);

const ATLANTIC_GOLF = new Set(["CYPD", "CYQY", "CYYG", "CYHZ"]);

export type LiveBaseInput = {
  homeIcao?: string | null;
  extraBases?: readonly string[] | null;
  bases?: readonly string[] | null;
};

export function uniqueIcaoList(codes: readonly (string | null | undefined)[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of codes) {
    const icao = normalizeIcao(raw);
    if (icao.length < 3 || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

/**
 * Primary + extras from the desk snapshot. Empty when the desk has not set a home.
 * Inlined so Inbox gravity does not import company.ts (that pulls duty / FTL
 * and cycles through charter realism while duty presets are still initializing).
 */
export function liveDeskBases(input: LiveBaseInput = {}): string[] {
  const listed = uniqueIcaoList(input.bases ?? []);
  const extrasRaw = uniqueIcaoList(input.extraBases ?? []);
  const homeRaw = normalizeIcao(input.homeIcao);
  const hasHomeOrExtra = "homeIcao" in input || "extraBases" in input;
  if (!hasHomeOrExtra && listed.length) {
    const homeIcao = listed[0]!;
    return [homeIcao, ...listed.slice(1).filter((icao) => icao !== homeIcao)];
  }
  const homeIcao = homeRaw || listed[0] || "";
  const extraBases = extrasRaw.filter((icao) => icao !== homeIcao);
  return homeIcao ? [homeIcao, ...extraBases] : extraBases;
}

export function isCanadaIcao(icao?: string | null): boolean {
  const code = normalizeIcao(icao);
  if (!code) return false;
  if (CANADA_REGIONS.has(destRegionOf(code))) return true;
  return code.startsWith("CY") || code.startsWith("CZ") || code.startsWith("CW");
}

export function basesTouchCanada(bases: readonly string[]): boolean {
  return uniqueIcaoList(bases).some((icao) => isCanadaIcao(icao));
}

type CharterTheater = "canada" | "us" | "sun" | "other";

function charterTheater(icao: string): CharterTheater {
  const code = normalizeIcao(icao);
  if (!code) return "other";
  if (isCanadaIcao(code)) return "canada";
  const region = destRegionOf(code);
  if (region === "us_fl" || region === "caribbean" || region === "mexico") return "sun";
  if (code.startsWith("K")) return "us";
  return "other";
}

function sharesTheater(dest: string, bases: readonly string[]): boolean {
  const theater = charterTheater(dest);
  return uniqueIcaoList(bases).some((base) => charterTheater(base) === theater);
}

/** Cape Breton / Atlantic golf country — only a live base here keeps Cabot gravity. */
export function basesLookAtlantic(bases: readonly string[]): boolean {
  return uniqueIcaoList(bases).some((icao) => ATLANTIC_GOLF.has(icao) || destRegionOf(icao) === "maritimes");
}

export function formatBaseList(bases: readonly string[]): string {
  const live = uniqueIcaoList(bases);
  return live.length ? live.join(", ") : "(set Home Base)";
}

function nearestBaseNm(icao: string, bases: readonly string[], scenery: SceneryAirport[]): number | null {
  const dest = resolveCoords(icao, scenery);
  if (!dest) return null;
  let best: number | null = null;
  for (const base of uniqueIcaoList(bases)) {
    const from = resolveCoords(base, scenery);
    if (!from) continue;
    const nm = haversineNm(from.lat, from.lon, dest.lat, dest.lon);
    if (best == null || nm < best) best = nm;
  }
  return best;
}

/**
 * Lower is better.
 * No live base: do not prefer the CYPD fiction field.
 * Live bases: sensible stage from the nearest base, with a soft penalty for
 * Canada when the company is not based in Canada (not a ban).
 */
export function destGravityPenalty(
  destIcao: string,
  bases: readonly string[],
  scenery: SceneryAirport[] = [],
): number {
  const dest = normalizeIcao(destIcao);
  const live = uniqueIcaoList(bases);
  if (!dest) return 0;
  if (!live.length) return dest === "CYPD" ? 12 : 0;
  // A current base is a weak dest (the origin is already that field) but it is
  // still this company's field — closer than the same ICAO flown from another theater.
  if (live.includes(dest)) return 7;
  const nm = nearestBaseNm(dest, live, scenery);
  const homeTheater = sharesTheater(dest, live);
  if (nm == null) return 14;
  if (nm < SHORT_STAGE_NM) return 10;
  if (nm <= HOME_REGION_NM) return homeTheater ? 0 : 6;
  if (nm <= MEDIUM_NM) return homeTheater ? 3 : 8;
  return homeTheater ? 6 : 12;
}

export function parkedFieldNearBases(
  parkedIcao: string | null | undefined,
  bases: readonly string[],
  scenery: SceneryAirport[] = [],
): boolean {
  const parked = normalizeIcao(parkedIcao);
  const live = uniqueIcaoList(bases);
  if (!parked || !live.length) return false;
  if (live.includes(parked)) return true;
  const nm = nearestBaseNm(parked, live, scenery);
  return nm != null && nm <= NEAR_BASE_ORIGIN_NM;
}

export function orderByBaseGravity<T extends { icao: string }>(
  rows: readonly T[],
  bases: readonly string[],
  scenery: SceneryAirport[] = [],
): T[] {
  return [...rows].sort((a, b) => {
    const pa = destGravityPenalty(a.icao, bases, scenery);
    const pb = destGravityPenalty(b.icao, bases, scenery);
    if (pa !== pb) return pa - pb;
    return a.icao.localeCompare(b.icao);
  });
}

/** Allow-list fields to name first in the Grok prompt. Excludes the bases themselves. */
export function nearBaseDestSample(
  scenery: readonly { icao: string }[],
  bases: readonly string[],
  cap = 12,
  sceneryRows: SceneryAirport[] = [],
): string[] {
  const live = new Set(uniqueIcaoList(bases));
  const seen = new Set<string>();
  const pool: string[] = [];
  for (const row of scenery) {
    const icao = normalizeIcao(row.icao);
    if (!icao || seen.has(icao) || live.has(icao)) continue;
    seen.add(icao);
    pool.push(icao);
  }
  pool.sort((a, b) => {
    const pa = destGravityPenalty(a, live.size ? [...live] : [], sceneryRows);
    const pb = destGravityPenalty(b, live.size ? [...live] : [], sceneryRows);
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });
  const near = pool.filter((icao) => destGravityPenalty(icao, [...live], sceneryRows) <= 8);
  const picked = near.length >= 4 ? near : pool;
  return picked.slice(0, cap);
}

/**
 * Soft-prefer list for Classic fallback. Scan ICAOs always stay so a Content.xml
 * gate can still file every legal field. Distant / former-home fields are not boosted.
 */
export function softPreferIcaos(input: {
  purposeDests: readonly string[];
  packIcaos?: readonly string[];
  scanIcaos?: readonly string[];
  bases: readonly string[];
  scenery?: SceneryAirport[];
}): string[] {
  const scenery = input.scenery ?? [];
  const bases = uniqueIcaoList(input.bases);
  const scan = uniqueIcaoList(input.scanIcaos ?? []);
  const candidates = uniqueIcaoList([...(input.purposeDests ?? []), ...(input.packIcaos ?? [])]);
  const ranked = [...candidates].sort(
    (a, b) => destGravityPenalty(a, bases, scenery) - destGravityPenalty(b, bases, scenery) || a.localeCompare(b),
  );
  const boosted = ranked.filter((icao) => destGravityPenalty(icao, bases, scenery) <= 6);
  const prefer = boosted.length >= 4 ? boosted : ranked.slice(0, 12);
  return uniqueIcaoList([...prefer, ...scan]);
}

export function grokDestinationGravityLine(bases: readonly string[], sample: readonly string[]): string {
  const where = formatBaseList(bases);
  const sampleBit = sample.length
    ? ` Near-base dest sample for most of this batch (allow-list only): ${sample.join(", ")}.`
    : "";
  return `Geographic variety: origins leave from the current company bases (${where}) or the suggested tail's assigned base when it is one of them. Most destinations are realistic charter demand within range of those bases.${sampleBit} Canada, the US, the Caribbean, and Mexico stay legal when the city-pair fits range and the allow-list. Do not ban Canada. Do not force US-only. Do not keep a frozen Port Hawkesbury / CYPD / Atlantic Canada set when no current base is there. Soft executive preference stays; a commercial hub is the backup.`;
}
